import { CONTENTFUL_BASE_URL, CONTENTFUL_ACCESS_TOKEN } from '../constants/contentful'

const get = (path) =>
  fetch(`${CONTENTFUL_BASE_URL}${path}&access_token=${CONTENTFUL_ACCESS_TOKEN}`)
    .then((r) => r.json())
    .catch((e) => { console.error('Contentful fetch error:', e); return null })

// Map a Contentful entry + its includes into WordPress-shaped object
const contentTypeToCategory = {
  project: 'PROJECT',
  news: 'NEWS',
  newsExternal: 'NEWS_EXTERNAL',
  page: 'PAGE',
}

const mapEntry = (entry, includes = []) => {
  const f = entry.fields
  if (!f) return null

  // resolve featured image URL
  let featuredImageUrl = ''
  if (f.featuredImage?.sys?.id) {
    const asset = includes.find((a) => a.sys.id === f.featuredImage.sys.id)
    if (asset?.fields?.file?.url) {
      featuredImageUrl = `https:${asset.fields.file.url}`
    }
  }

  const contentType = entry.sys.contentType?.sys?.id || ''
  const category = contentTypeToCategory[contentType] || f.category || ''

  return {
    id: f.wordpressId || entry.sys.id,
    _contentfulId: entry.sys.id,
    title: { rendered: f.title || '' },
    content: { rendered: f.content || '' },
    excerpt: { rendered: f.excerpt ? `<p>${f.excerpt}</p>` : '' },
    jetpack_featured_media_url: featuredImageUrl,
    tags: f.tags || [],
    date: f.publishDate || entry.sys.createdAt,
    categories: [],
    _category: category,
  }
}

// Fetch all assets from includes
const getIncludes = (data) => (data?.includes?.Asset || [])

// ── public API ────────────────────────────────────────────────────────────────

// Map category string to Contentful content type id
const categoryToContentType = (category) => {
  const map = {
    PROJECT: 'project',
    NEWS: 'news',
    NEWS_EXTERNAL: 'newsExternal',
    'NEWS,NEWS_EXTERNAL': null, // multi — handled separately
  }
  return map[category] || 'page'
}

export const fetchPostsByCategory = async (category, page = 1, perPage = 60, callback) => {
  const skip = (page - 1) * perPage

  // multi-category fetch (news + newsExternal)
  if (category.includes(',')) {
    const categories = category.split(',').map((c) => c.trim())
    const results = await Promise.all(
      categories.map((cat) =>
        get(`/entries?content_type=${categoryToContentType(cat)}&skip=0&limit=250&include=1&order=-fields.publishDate`)
      )
    )
    const includes = results.flatMap(getIncludes)
    const posts = results
      .flatMap((d) => (d?.items || []).map((e) => mapEntry(e, includes)))
      .filter(Boolean)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(skip, skip + perPage)
    return callback(posts, posts.length)
  }

  const contentType = categoryToContentType(category)
  const data = await get(
    `/entries?content_type=${contentType}&skip=${skip}&limit=${perPage}&include=1&order=-fields.publishDate`
  )
  if (!data) return callback([], 0)

  const includes = getIncludes(data)
  const posts = (data.items || []).map((e) => mapEntry(e, includes)).filter(Boolean)
  callback(posts, data.total || posts.length)
}

export const fetchPostsByDateAndCategory = async (category, page, perPage, otherParams, callback) => {
  // extract year from WordPress-style "&after=YYYY-01-01...&before=YYYY-12-31..."
  const yearMatch = otherParams && otherParams.match(/after=(\d{4})/)
  const year = yearMatch ? yearMatch[1] : null

  if (!year) {
    return fetchPostsByCategory(category, page, perPage, callback)
  }

  // fetch all then filter by year
  fetchPostsByCategory(category, 1, 250, (posts) => {
    const filtered = posts.filter((p) => {
      const d = p.date ? new Date(p.date) : null
      return d && d.getFullYear() === parseInt(year, 10)
    })
    callback(filtered, filtered.length)
  })
}

export const fetchPostsSearch = async (category, search, page, perPage, callback) => {
  const contentType = categoryToContentType(category)
  const data = await get(
    `/entries?content_type=${contentType}&limit=250&include=1&order=-fields.publishDate`
  )
  if (!data) return callback([], 0)

  const includes = getIncludes(data)
  const all = (data.items || []).map((e) => mapEntry(e, includes)).filter(Boolean)
  const lower = search.toLowerCase()
  const filtered = all.filter(
    (p) =>
      p.title.rendered.toLowerCase().includes(lower) ||
      p.content.rendered.toLowerCase().includes(lower)
  )
  const start = (page - 1) * perPage
  callback(filtered.slice(start, start + perPage), filtered.length)
}

export const getPostById = async (wordpressId, callback) => {
  const types = ['project', 'news', 'newsExternal', 'page']
  // Try all types in parallel instead of sequentially
  const results = await Promise.all(
    types.map((type) =>
      get(`/entries?content_type=${type}&fields.wordpressId=${wordpressId}&limit=1&include=1`)
    )
  )
  for (const data of results) {
    if (data?.items?.length) {
      const includes = getIncludes(data)
      return callback(mapEntry(data.items[0], includes))
    }
  }
  callback(null)
}

export const fetchAllTags = async (callback) => {
  const data = await get(`/entries?content_type=project&limit=250&select=fields.tags`)
  if (!data) return callback([])

  const tagSet = new Set()
  ;(data.items || []).forEach((e) => {
    (e.fields?.tags || []).forEach((t) => tagSet.add(t))
  })

  // Return in same shape as WordPress tags API: [{ id, name }]
  const tags = Array.from(tagSet).map((name) => ({ id: name, name }))
  callback(tags)
}
